import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faSitemap, faPen, faArrowRightLong, faBolt, faPlay, faCircleInfo, faMicrophone, faVolumeHigh, faHandPointer } from "@fortawesome/free-solid-svg-icons";
import { SCHEMES, SRS_CELLS, SEMISTATIONS, LINES, transitionSteps, scopeHasGenerator, stateClassOf, stateLabelOf, isStateValidForScope, statePath, stateJourney } from "../../Functions/htScenarios";
import { Dropdown, OptGroup, StateChip, StatePicker, buildCellGroups } from "../CreateTrainingSession/CreateSessionFields";
import "../CreateTrainingSession/CreateTrainingSession.css";
import "./CreateLearningSession.css";

// Create form of the "Învățare" module. Same configuration as the training
// module (station, scope, initial + final operative state), but the run itself
// is dictated: there is no free-practice option, since every exercise has to
// have a maneuver program to dictate.
const CreateLearningSessionForm = ({ data } : { data: any }) => {
    const navigate = useNavigate();
    const [cookies] = useCookies();
    const en = cookies?.language === "en";

    const [scheme, setScheme] = useState<string>("");
    const [initialState, setInitialState] = useState<string>("");
    const [finalState, setFinalState] = useState<string>("");
    const [cell, setCell] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [nameTouched, setNameTouched] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const isTeacher = data?.userData?.type === "teacher";
    const bothStates = !!initialState && !!finalState;
    const sameState = bothStates && stateClassOf(initialState) === stateClassOf(finalState);
    const missingFields = !scheme || !cell || !bothStates;

    const schemeGroups: OptGroup[] = useMemo(() => ([
        { items: SCHEMES.map((s) => ({ id: s.id, label: s.enabled ? s.label : `${s.label}${en ? " (soon)" : " (în curând)"}`, disabled: !s.enabled })) },
    ]), [en]);
    const cellGroups: OptGroup[] = useMemo(() => buildCellGroups(SRS_CELLS, LINES, en), [en]);

    useEffect(() => {
        if (!cell) return;
        if (initialState && !isStateValidForScope(initialState, cell)) setInitialState("");
        if (finalState && !isStateValidForScope(finalState, cell)) setFinalState("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cell]);

    const scopeDisplay = (id: string): string => {
        const c = SRS_CELLS.find((x) => x.id === id);
        if (c) return c.label.replace(/\s*\(.*\)$/, "");
        const opt = cellGroups.flatMap((g) => g.items).find((o) => o.id === id);
        return opt?.label || id;
    };
    const autoName = useMemo(() => (
        [scheme || null, cell ? scopeDisplay(cell) : null,
            bothStates ? `${stateLabelOf(initialState)} → ${stateLabelOf(finalState)}` : null]
            .filter(Boolean).join(" — ")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [scheme, initialState, finalState, cell, bothStates]);
    useEffect(() => { if (!nameTouched) setName(autoName); }, [autoName, nameTouched]);

    const displayName = () => (name.trim() ? name.trim() : autoName);
    // The exercise climbs the operative-state ladder one rung at a time and then
    // comes back, so the preview shows every state it passes through and the
    // switch changes of each leg.
    const journey = (!bothStates || sameState) ? [] : stateJourney(initialState, finalState);
    const targetIdx = (!bothStates || sameState) ? -1 : statePath(initialState, finalState).length - 1;
    const steps = (!bothStates || sameState) ? [] : statePath(initialState, finalState)
        .slice(0, -1)
        .flatMap((from, i, arr) => transitionSteps(
            stateClassOf(from), stateClassOf(statePath(initialState, finalState)[i + 1]), scopeHasGenerator(cell),
        ).map((st) => ({ ...st, key: `${i}-${st.key}` })));

    const createSession = async () => {
        if (busy) return;
        if (missingFields) {
            setShowErrors(true);
            return setError(en ? "Fill in every field first." : "Completează toate câmpurile.");
        }
        if (sameState) {
            setShowErrors(true);
            return setError(en ? "Initial and final state are the same — nothing to maneuver." : "Starea inițială și cea finală sunt identice — nu există manevre de efectuat.");
        }
        setShowErrors(false);
        setBusy(true); setError("");
        try {
            const semiStation = SEMISTATIONS.some((s) => s.id === cell) ? cell : "";
            // Both states live in the `scenario` column as "initial>final".
            const scenario = `${initialState}>${finalState}`;
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-htlearning/create-session-htlearning`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: data.userData.userId, schemeName: scheme, semiStation, cell, scenario, name: displayName(), problems: { scheme, cell, initialState, finalState } }),
            });
            const response = await request.json();
            if (!response.success) { setError(en ? "Could not create the session." : "Sesiunea nu a putut fi creată."); setBusy(false); return; }
            const sName = displayName();
            window.history.replaceState({ sessionName: sName, ownerId: data.userData.userId }, "");
            if (isTeacher) navigate(`/session-htlearning/${response.sessionId}/leaderboard`, { state: { sessionName: sName } });
            else navigate(`/session-htlearning/${response.sessionId}/0`, { state: { sessionName: sName, ownerId: data.userData.userId } });
        } catch (e) {
            setError(en ? "Could not create the session." : "Sesiunea nu a putut fi creată."); setBusy(false);
        }
    };

    return (
        <div className="cts-form">
            <p className="cts-intro cts-span">
                {en
                    ? "Configure a guided exercise on the SRS station: the dispatcher dictates every maneuver, you read it back out loud and only then perform it on the scheme."
                    : "Configurează un exercițiu ghidat pe stația SRS: dispecerul dictează fiecare manevră, o confirmi cu voce tare și abia apoi o execuți pe schemă."}
            </p>

            <Dropdown label={en ? "Station" : "Stație"} icon={faLayerGroup} value={scheme} groups={schemeGroups} onChange={setScheme}
                placeholder={en ? "Choose a station" : "Alege o stație"} invalid={showErrors && !scheme} />
            <Dropdown label={en ? "Cell / semi-station" : "Celulă / semistație"} icon={faSitemap} value={cell} groups={cellGroups} onChange={setCell}
                placeholder={en ? "Choose a cell / semi-station" : "Alege o celulă / semistație"} invalid={showErrors && !cell} matrix />
            <StatePicker label={en ? "Initial state" : "Starea inițială"} value={initialState} onChange={setInitialState}
                isValid={(id) => !cell || isStateValidForScope(id, cell)}
                placeholder={en ? "Choose the initial state" : "Alege starea inițială"}
                invalid={showErrors && (!initialState || sameState)} anchor="left" />
            <StatePicker label={en ? "Final state" : "Starea finală"} value={finalState} onChange={setFinalState}
                isValid={(id) => !cell || isStateValidForScope(id, cell)}
                placeholder={en ? "Choose the final state" : "Alege starea finală"}
                invalid={showErrors && (!finalState || sameState)} anchor="right" />

            <label className="cts-field cts-span">
                <span><FontAwesomeIcon icon={faPen} className="cts-label-icon" />{en ? "Session name" : "Numele sesiunii"}</span>
                <input type="text" value={name} placeholder={autoName || (en ? "Session name" : "Numele sesiunii")}
                    onChange={(e) => { setNameTouched(true); setName(e.target.value); }} />
            </label>

            {/* How a dictated run works — the three beats of every single step. */}
            <div className="cls-flow cts-span">
                <div className="cls-flow-step">
                    <FontAwesomeIcon icon={faVolumeHigh} />
                    <b>1. {en ? "The order" : "Dispozitia"}</b>
                    <span>{en ? "D.E.I. dictates the maneuver." : "D.E.I. dictează manevra."}</span>
                </div>
                <FontAwesomeIcon icon={faArrowRightLong} className="cls-flow-arrow" />
                <div className="cls-flow-step">
                    <FontAwesomeIcon icon={faMicrophone} />
                    <b>2. {en ? "The read-back" : "Confirmarea"}</b>
                    <span>{en ? "You repeat it out loud; it is checked." : "O repeți cu voce tare; este verificată."}</span>
                </div>
                <FontAwesomeIcon icon={faArrowRightLong} className="cls-flow-arrow" />
                <div className="cls-flow-step">
                    <FontAwesomeIcon icon={faHandPointer} />
                    <b>3. {en ? "The execution" : "Executia"}</b>
                    <span>{en ? "You operate that switch on the scheme." : "Execuți manevra pe schemă."}</span>
                </div>
            </div>

            <div className="cts-transition cts-span">
                <div className="cts-transition-head">
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <span>{bothStates
                        ? `${stateLabelOf(initialState)} → ${stateLabelOf(finalState)}`
                        : (en ? "Transition" : "Tranzitia")}</span>
                </div>

                {!bothStates ? (
                    <p className="cts-transition-desc">
                        {en
                            ? "Pick a cell, an initial state and a final state to see the maneuvers that will be dictated."
                            : "Alege o celulă, o stare inițială și o stare finală pentru a vedea manevrele care vor fi dictate."}
                    </p>
                ) : sameState ? (
                    <p className="cts-transition-desc" style={{ color: "#c0392b", fontWeight: 700 }}>
                        {en ? "Initial and final state are the same — nothing to maneuver." : "Starea inițială și cea finală sunt identice — nu există manevre de efectuat."}
                    </p>
                ) : (
                    <>
                        <div className="cts-transition-flow">
                            <StateChip title={en ? "From" : "Din starea"} state={stateLabelOf(initialState)} />
                            <FontAwesomeIcon icon={faArrowRightLong} className="cts-transition-arrow" />
                            <StateChip title={en ? "To" : "În starea"} state={stateLabelOf(finalState)} />
                        </div>

                        {/* The exercise climbs the ladder one rung at a time and comes
                            back, so the whole route is spelled out as a breadcrumb. */}
                        <div className="cts-journey">
                            <span className="cts-journey-kicker">{en ? "Route" : "Parcurs"}</span>
                            <div className="cts-journey-path">
                                {journey.map((st, i) => (
                                    <Fragment key={`${i}-${st}`}>
                                        {i ? <span className="cts-journey-arrow">→</span> : null}
                                        <span className={`cts-journey-state${i === targetIdx ? " target" : ""}`}>
                                            {stateLabelOf(st)}
                                        </span>
                                    </Fragment>
                                ))}
                            </div>
                        </div>
                        {steps.length ? (
                            <div className="cts-transition-steps">
                                {steps.map((s) => (
                                    <div className="cts-step" key={s.key}>
                                        <FontAwesomeIcon icon={faBolt} className="cts-step-icon" />
                                        <span className="cts-step-label">{s.label}</span>
                                        <span className={`cts-badge ${s.from}`}>{s.from}</span>
                                        <FontAwesomeIcon icon={faArrowRightLong} className="cts-step-arrow" />
                                        <span className={`cts-badge ${s.to}`}>{s.to}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            {error ? <p className="cts-error cts-span">{error}</p> : null}

            <button type="button" className="cts-submit cts-span" disabled={busy} onClick={createSession}>
                <FontAwesomeIcon icon={faPlay} className="cts-submit-icon" />
                {busy ? (en ? "Creating..." : "Se creează...") : (en ? "Create session" : "Creează sesiunea")}
            </button>
        </div>
    );
};

export default CreateLearningSessionForm;
